/**
 * The Grow settings and the server's limits the app reads: the server's last
 * answer, kept on the device, and nothing of the app's own.
 *
 * The owner changes the settings in the backend's Grow Settings panel
 * (admin/grow-settings.html); the server also sends the limits its validators
 * enforce (the longest bed name, the most photos in a share, the word that
 * confirms a delete), as `limits.<NAME>` beside the settings in the same
 * answer (GET /grow-settings; services/grow/appLimits). This module holds no
 * default for any of them (architecture audit A64 and A65, batch 11a): a
 * second set drifts from the first, and the app's had (five of its defaults
 * differed from the database's).
 *
 * What it does hold is the last answer. It is kept on the device
 * (theme/themeTokens readKeptSettings and keepSettings, the first-paint
 * plumbing, public settings and not the gardener's data), read as the app
 * starts, so a repeat visit draws with the owner's values at once, and a
 * request that fails leaves it standing. The request is asked as the app
 * starts (main.tsx loadGrowSettings) and again when a tab left in the
 * background becomes visible, at most once every few minutes
 * (rereadGrowSettingsWhenVisible; B103), so an open tab hears a changed
 * setting. A failure on our side is reported to Sentry
 * (lib/serverFailure); the gardener is told nothing, since the kept answer
 * (or, on a first-ever open, drawing without the missing values) is what
 * they see, and no "try again" is offered.
 *
 * On a first-ever open with no kept answer there is no value until the answer
 * arrives: growSetting and growLimit give undefined, and the reader draws
 * without it (a text box without a maxLength, the sign-in page without the
 * Google One Tap, the root text at the browser's own size) rather than from a
 * guess. useGrowSettings draws a component again when the answer arrives.
 *
 * The answer is the short list (W24 Job C): only the keys in KINDS below, the
 * painting positions (today.PAINTING_<key>) and the limits; the server's other
 * settings (prices, trial lengths, warning sentences, danger codes, the
 * good-day thresholds) stay on the server, and the admin Grow-settings screen
 * reads the full set on its own route. KINDS is the one list the server's copy
 * is checked against. The answer carries a `version` and an ETag with
 * Cache-Control no-cache, so the browser asks again with If-None-Match and an
 * unchanged answer is a 304 with no body; nothing here reads the version
 * (it is not a key the app reads, so mergeSent leaves it out).
 *
 * Each value is checked against the kind its key has as it arrives (a number
 * for a number, text for text, on or off for a switch, a list of words for a
 * list), and one that does not fit is left out, as is any key the app does
 * not read. The tables (the share card grounds, the caption starter's season
 * emojis) are checked part by part.
 *
 * The warnings' sentences, the frost alert's words and the dangers are the
 * server's to say and read (they are not here any more); which marks make
 * Today's jobs is Grow's own To do rules, read by the backend.
 *
 * To add a setting the app reads: give it a definition on the server
 * (services/grow/growSettings, with `app: true` or in an app section), add its
 * key and kind to GrowSettingValues and KINDS below, then read it with
 * useGrowSettings or growSetting. To add a limit: build it in the server's
 * services/grow/appLimits from the constant its validator reads, add its name
 * and type to GrowLimits and KINDS, then read it with useGrowLimits or
 * growLimit.
 *
 * Who uses it: main.tsx (loadGrowSettings) and Today, Weather, the plant
 * cards' chips, the photo tray, sharing, the forms with a limit, the welcome
 * and sign-in screens (see each import).
 *
 * @module growSettings
 */

import { useMemo, useSyncExternalStore } from 'react';
import { getGrowSettings } from '@/shared/api/growSettings';
import { reportServerFailure, isServerFailure } from '@/shared/lib/serverFailure';
import { readKeptSettings, keepSettings } from '@/shared/theme/themeTokens';

/** A share card ground's colour and the colour of the Grow mark and handle on it, as "#RRGGBB". */
export interface GroundColours {
  /** The ground. */
  fill: string;
  /** The mark and handle on it. */
  ink: string;
}

/** The Share caption starter's emoji for one season, as the settings keep it (a part of its own so the panel can draw it). */
export interface SeasonEmoji {
  /** The emoji. */
  emoji: string;
}

/** The four seasons the caption starter has an emoji for. */
export type CaptionSeason = 'spring' | 'summer' | 'fall' | 'winter';

/**
 * The Grow settings the app reads, by the key the panel and the backend use.
 * Every one is optional: it is there once the server has said it (now or on an
 * earlier visit). Degrees are Fahrenheit.
 */
export interface GrowSettingValues {
  /** How many days ahead Today's Coming up looks, unless the gardener chose their own: 0 to 7. */
  'today.HEADS_UP_DAYS'?: number;
  /** The wettest or driest month is said on the first this many days Today is opened in it; 0 never. */
  'today.RAIN_MONTH_OPENS'?: number;
  /** Where each seasonal painting is cropped in the picture setting's 5:2 band, as its vertical position: 0 the top, 100 the bottom (today.PAINTING_<key>). */
  [painting: `today.PAINTING_${string}`]: number | undefined;
  /** The longest side, in pixels, of a photo the server takes (it refuses a larger one with 413 PHOTO_TOO_LARGE_PIXELS). */
  'photos.MAX_EDGE_PX'?: number;
  /** Google One Tap ("Continue as …") on the sign-in page. */
  'welcome.GOOGLE_ONE_TAP'?: boolean;
  /** The Facebook button on the sign-in page. */
  'welcome.FACEBOOK_SIGNIN'?: boolean;
  /** The share card grounds, by the names gardeners' saved choices keep. */
  'sharing.GROUNDS'?: Record<string, GroundColours>;
  /** The share size, layout and card ground a gardener starts with. */
  'sharing.DEFAULT_SIZE'?: string;
  'sharing.DEFAULT_LAYOUT'?: string;
  'sharing.DEFAULT_GROUND'?: string;
  /** What a new plant share (or a season review) prints to start: the switches that are on (name, variety, date, words, handle). */
  'sharing.DEFAULT_ELEMENTS'?: string[];
  /** What a new bed share's own pictures print to start: the switches that are on (name, words, handle, count). */
  'sharing.DEFAULT_COVER_ELEMENTS'?: string[];
  /** What the photos of a bed's plants print to start: the switches that are on (name, variety, date, words, handle, place). */
  'sharing.DEFAULT_PLANT_PHOTO_ELEMENTS'?: string[];
  /** The Share caption starter's emoji for the season it is now, after the crop's own. */
  'sharing.CAPTION_SEASON_EMOJI'?: Record<CaptionSeason, SeasonEmoji>;
  /** The general hashtags the caption starter ends with (lowercase, no #), after the crop's own and the season's. */
  'sharing.CAPTION_TAGS'?: string[];
  /** The most hashtags the caption starter suggests. */
  'sharing.CAPTION_TAGS_MAX'?: number;
  /** The colour a new stage, and a new note shortcut, of the gardener's own starts with: a key of the colour palette (batch 13f, F1 and F12). */
  'plants.NEW_STAGE_COLOR'?: string;
  'notes.NEW_SHORTCUT_COLOR'?: string;
  /** The everyday words a to-do starts with ("plant", "the"), one list a language, left out of the plant search when a to-do's words are moved to Add a plant (E18). */
  'plants.SEARCH_FILLER_EN'?: string[];
  'plants.SEARCH_FILLER_ES'?: string[];
  /** The root text size, in pixels, for each of the three text sizes (A67). */
  'ui.ROOT_FONT_SMALL_PX'?: number;
  'ui.ROOT_FONT_MEDIUM_PX'?: number;
  'ui.ROOT_FONT_LARGE_PX'?: number;
}

/** A setting's key, e.g. "today.RAIN_MONTH_OPENS". */
export type GrowSettingKey = keyof GrowSettingValues;

/**
 * The limits the server enforces, by name (the answer's `limits.<NAME>`), each
 * the very constant its validator reads (backend services/grow/appLimits).
 * Every one is optional: there once the server has said it.
 */
export interface GrowLimits {
  /** The longest a bed's name and description may be. */
  LOCATION_NAME_MAX?: number;
  LOCATION_DESCRIPTION_MAX?: number;
  /** The longest a stage's name may be. */
  STAGE_NAME_MAX?: number;
  /** The longest a variety's name may be. */
  VARIETY_NAME_MAX?: number;
  /** The longest the name a gardener appears under in the gallery may be. */
  GALLERY_NAME_MAX?: number;
  /** The most photos one share carries. */
  SHARE_MAX_PHOTOS?: number;
  /** The longest a note may be. */
  NOTE_MAX_LENGTH?: number;
  /** The longest a gardener's own to-do's title may be (D50; the server's todo.OWN_TITLE_MAX). */
  OWN_TODO_TITLE_MAX?: number;
  /** The longest a quick note pill's label may be. */
  SHORTCUT_LABEL_MAX?: number;
  /** The longest the concern a gardener tells the Plant Doctor may be. */
  PLANT_DOCTOR_CONCERN_MAX_CHARS?: number;
  /** The longest a gift's message may be. */
  GIFT_MESSAGE_MAX?: number;
  /** The most photos one guess in the tray may ask about. */
  TRAY_GUESS_BATCH?: number;
  /** The margin, as a share of the photo, a seed packet's cut is made with. */
  PACKET_CROP_MARGIN?: number;
  /** The word a gardener types to confirm deleting a plant or their account. */
  DELETE_CONFIRM_WORD?: string;
  /** The largest photo upload, in megabytes, and the kinds of picture taken. */
  PHOTO_MAX_MB?: number;
  PHOTO_TYPES?: string[];
  /** The fewest and the most days a gardener may choose for Coming up (today.HEADS_UP_DAYS's min and max, as the server saves them). */
  COMING_UP_MIN_DAYS?: number;
  COMING_UP_MAX_DAYS?: number;
  /** Where photos are served from (only when the server names its bucket). */
  SPACES_BASE_URL?: string;
}

/** A limit's name, e.g. "LOCATION_NAME_MAX". */
export type GrowLimitName = keyof GrowLimits;

/** The kinds a value can be checked as. */
type Kind = 'number' | 'boolean' | 'text' | 'texts' | 'grounds' | 'seasonEmoji';

/** The kind of each setting the app reads, by key; the checks above all of it. */
const KINDS: Record<string, Kind> = {
  'today.HEADS_UP_DAYS': 'number',
  'today.RAIN_MONTH_OPENS': 'number',
  'photos.MAX_EDGE_PX': 'number',
  'welcome.GOOGLE_ONE_TAP': 'boolean',
  'welcome.FACEBOOK_SIGNIN': 'boolean',
  'sharing.GROUNDS': 'grounds',
  'sharing.DEFAULT_SIZE': 'text',
  'sharing.DEFAULT_LAYOUT': 'text',
  'sharing.DEFAULT_GROUND': 'text',
  'sharing.DEFAULT_ELEMENTS': 'texts',
  'sharing.DEFAULT_COVER_ELEMENTS': 'texts',
  'sharing.DEFAULT_PLANT_PHOTO_ELEMENTS': 'texts',
  'sharing.CAPTION_SEASON_EMOJI': 'seasonEmoji',
  'sharing.CAPTION_TAGS': 'texts',
  'sharing.CAPTION_TAGS_MAX': 'number',
  'plants.NEW_STAGE_COLOR': 'text',
  'notes.NEW_SHORTCUT_COLOR': 'text',
  'plants.SEARCH_FILLER_EN': 'texts',
  'plants.SEARCH_FILLER_ES': 'texts',
  'ui.ROOT_FONT_SMALL_PX': 'number',
  'ui.ROOT_FONT_MEDIUM_PX': 'number',
  'ui.ROOT_FONT_LARGE_PX': 'number',
  'limits.LOCATION_NAME_MAX': 'number',
  'limits.LOCATION_DESCRIPTION_MAX': 'number',
  'limits.STAGE_NAME_MAX': 'number',
  'limits.VARIETY_NAME_MAX': 'number',
  'limits.GALLERY_NAME_MAX': 'number',
  'limits.SHARE_MAX_PHOTOS': 'number',
  'limits.NOTE_MAX_LENGTH': 'number',
  'limits.OWN_TODO_TITLE_MAX': 'number',
  'limits.SHORTCUT_LABEL_MAX': 'number',
  'limits.PLANT_DOCTOR_CONCERN_MAX_CHARS': 'number',
  'limits.GIFT_MESSAGE_MAX': 'number',
  'limits.TRAY_GUESS_BATCH': 'number',
  'limits.PACKET_CROP_MARGIN': 'number',
  'limits.DELETE_CONFIRM_WORD': 'text',
  'limits.PHOTO_MAX_MB': 'number',
  'limits.PHOTO_TYPES': 'texts',
  'limits.COMING_UP_MIN_DAYS': 'number',
  'limits.COMING_UP_MAX_DAYS': 'number',
  'limits.SPACES_BASE_URL': 'text',
};

/** The key prefix of the seasonal paintings' positions, each a number (today.PAINTING_<key>). */
const PAINTING_PREFIX = 'today.PAINTING_';

/** A colour as the panel saves it. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

/** The four seasons, for checking the caption starter's emojis. */
const SEASONS: readonly CaptionSeason[] = ['spring', 'summer', 'fall', 'winter'];

/** What is in use: the checked answer, as a map by full key. */
type Held = Record<string, unknown>;

/**
 * The share card grounds as sent, each only when both its colours are
 * "#RRGGBB"; a ground that does not fit is left out.
 *
 * @param sent - As the server sent it.
 * @returns The grounds that fit, or undefined when none does.
 */
function groundsFrom(sent: unknown): Record<string, GroundColours> | undefined {
  if (!sent || typeof sent !== 'object' || Array.isArray(sent)) return undefined;
  const grounds: Record<string, GroundColours> = {};
  for (const [name, colours] of Object.entries(sent as Record<string, Partial<GroundColours> | undefined>)) {
    if (colours && HEX.test(String(colours.fill)) && HEX.test(String(colours.ink))) {
      grounds[name] = { fill: String(colours.fill), ink: String(colours.ink) };
    }
  }
  return Object.keys(grounds).length > 0 ? grounds : undefined;
}

/**
 * The caption starter's season emojis as sent: each season's only when it is
 * text with something in it.
 *
 * @param sent - As the server sent it.
 * @returns The seasons that fit, or undefined when none does.
 */
function seasonEmojiFrom(sent: unknown): Record<CaptionSeason, SeasonEmoji> | undefined {
  if (!sent || typeof sent !== 'object' || Array.isArray(sent)) return undefined;
  const emojis: Partial<Record<CaptionSeason, SeasonEmoji>> = {};
  for (const season of SEASONS) {
    const set = (sent as Record<string, Partial<SeasonEmoji> | undefined>)[season];
    const emoji = typeof set?.emoji === 'string' ? set.emoji.trim() : '';
    if (emoji) emojis[season] = { emoji };
  }
  return Object.keys(emojis).length > 0 ? (emojis as Record<CaptionSeason, SeasonEmoji>) : undefined;
}

/**
 * One value as its kind allows, or undefined when it does not fit.
 *
 * @param kind - What the key's value should be.
 * @param value - As the server sent it (or as it was kept).
 * @returns The value, or undefined.
 */
function checked(kind: Kind, value: unknown): unknown {
  switch (kind) {
    case 'number': return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    case 'boolean': return typeof value === 'boolean' ? value : undefined;
    case 'text': return typeof value === 'string' && value !== '' ? value : undefined;
    case 'texts': return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : undefined;
    case 'grounds': return groundsFrom(value);
    case 'seasonEmoji': return seasonEmojiFrom(value);
    default: return undefined;
  }
}

/**
 * The answer, keeping only the keys the app reads and only the values that
 * are the kind their key has.
 *
 * @param sent - Each setting and limit by its full key, as the server sent them.
 * @returns What fits.
 */
function mergeSent(sent: Record<string, unknown>): Held {
  const held: Held = {};
  for (const [key, value] of Object.entries(sent ?? {})) {
    const kind: Kind | undefined = KINDS[key] ?? (key.startsWith(PAINTING_PREFIX) ? 'number' : undefined);
    if (!kind) continue;
    const fits = checked(kind, value);
    if (fits !== undefined) held[key] = fits;
  }
  return held;
}

/**
 * The answer kept on the device, checked again as if it had just arrived.
 *
 * @returns What was kept and still fits, or nothing.
 */
function heardBefore(): Held {
  const text = readKeptSettings();
  if (!text) return {};
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? mergeSent(parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** What is in use: the kept answer until the server's arrives. */
let current: Held = heardBefore();

/** The components drawn from it, to draw again when the answer arrives. */
const listeners = new Set<() => void>();

/** The request for the answer, once started. */
let started: Promise<void> | null = null;

/**
 * The fewest milliseconds between two asks when a tab becomes visible again (five minutes).
 *
 * @kept K1
 */
const REREAD_MIN_MS = 5 * 60 * 1000;

/** When the answer was last asked for (milliseconds), 0 before the first ask. */
let lastAskedAt = 0;

/** Whether the visibility listener is already set, so setting it twice adds one. */
let rereadSet = false;

/**
 * Asks the server for the settings and limits. The answer replaces what is in
 * use and is kept for the next open. If the request fails the kept answer
 * stands, and a failure on our side is reported to Sentry.
 *
 * @returns The request, settled whether it worked or not.
 */
function ask(): Promise<void> {
  lastAskedAt = Date.now();
  return getGrowSettings()
    .then((sent) => {
      current = mergeSent(sent);
      keepSettings(JSON.stringify(current));
      listeners.forEach((listener) => listener());
    })
    .catch((error: unknown) => {
      // The kept answer stands and the gardener sees nothing different; the failure is ours to hear.
      if (isServerFailure(error)) reportServerFailure(error, 'Grow settings');
    });
}

/**
 * Asks the server for the settings and limits, once; later calls share the
 * first (the re-read below asks again by itself).
 *
 * @returns The one request, settled whether it worked or not.
 */
export function loadGrowSettings(): Promise<void> {
  started ??= ask();
  return started;
}

/**
 * Reads the settings again when the tab becomes visible, at most once every
 * REREAD_MIN_MS, through the same request as the first read: a tab left open
 * hears a setting the owner changed. Set once as the app starts (main.tsx);
 * a second call changes nothing. Nothing of the gardener's is kept.
 */
export function rereadGrowSettingsWhenVisible(): void {
  if (rereadSet) return;
  rereadSet = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastAskedAt < REREAD_MIN_MS) return;
    started = ask();
  });
}

/**
 * Keeps a component told of the answer's arrival.
 *
 * @param listener - Called when it changes.
 * @returns What stops it.
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Calls back when the answer arrives, for code outside React that applies a
 * setting to the page (the root text size).
 *
 * @param listener - Called when it changes.
 * @returns What stops it.
 */
export const onGrowSettings = subscribe;

/** The values in use now, a new object each time the answer changes. */
const snapshot = (): Held => current;

/**
 * One setting as it is now, for code outside React.
 *
 * @param key - e.g. "photos.MAX_EDGE_PX".
 * @returns The server's value (this visit's or the last kept), or undefined while the app has not heard it.
 */
export function growSetting<K extends GrowSettingKey>(key: K): GrowSettingValues[K] {
  return current[key] as GrowSettingValues[K];
}

/**
 * One limit as it is now, for code outside React.
 *
 * @param name - e.g. "LOCATION_NAME_MAX".
 * @returns The server's limit, or undefined while the app has not heard it (a box then has no maxLength and the server's refusal stands).
 */
export function growLimit<N extends GrowLimitName>(name: N): GrowLimits[N] {
  return current[`limits.${name}`] as GrowLimits[N];
}

/**
 * Every setting, for a component: drawn again when the answer arrives.
 *
 * @returns The settings in use; each is undefined until the server has said it.
 */
export function useGrowSettings(): GrowSettingValues {
  return useSyncExternalStore(subscribe, snapshot, snapshot) as GrowSettingValues;
}

/**
 * Every limit, for a component: drawn again when the answer arrives.
 *
 * @returns The limits in use; each is undefined until the server has said it.
 */
export function useGrowLimits(): GrowLimits {
  const held = useSyncExternalStore(subscribe, snapshot, snapshot);
  return useMemo(() => Object.fromEntries(
    Object.entries(held).filter(([key]) => key.startsWith('limits.')).map(([key, value]) => [key.slice('limits.'.length), value]),
  ) as GrowLimits, [held]);
}
